import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, InputGroup, Modal, Table, Tab, Tabs } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { setIsLoading } from '../general/actions';
import axiosInstance from '../general/axiosInstance';
import { currencyFormater } from '../general/utils';
import PaymentFormModal from '../payments/paymentFormModal';
import { Pager, SOURCE_LABEL, StatusBadge, apiError, formatDate, formatDateTime } from '../payments/paymentUtils';

/**
 * Cuentas por pagar (solo superadmin; el backend responde 403 al resto).
 * Reportes: confirmar/rechazar. Contratos: encender/apagar el aviso y registrar pagos directos.
 */
function AdminPayments({ roles, setIsLoading }) {
    const allowed = (roles || []).includes('superadmin');
    const [message, setMessage] = useState(null); // { variant, text }

    // Casilla "cerrar sesiones del proveedor": se reinicia cada vez que se abre una acción.
    const [closeSessions, setCloseSessions] = useState(false);

    // ---- Filtro por proveedor (compartido por ambas pestañas) ----
    const [providers, setProviders] = useState([]);
    const [providerId, setProviderId] = useState('');

    // ---- Reportes ----
    const [status, setStatus] = useState('pending');
    const [reports, setReports] = useState(null);
    const [review, setReview] = useState(null); // { report, decision }
    const [reviewNote, setReviewNote] = useState('');
    const [reviewing, setReviewing] = useState(false);

    // ---- Contratos ----
    const [filter, setFilter] = useState('');
    const [query, setQuery] = useState('');
    const [agreements, setAgreements] = useState(null);
    const [flagTarget, setFlagTarget] = useState(null); // { agreement, turnOn }
    const [flagNote, setFlagNote] = useState('');
    const [flagSaving, setFlagSaving] = useState(false);
    const [payTarget, setPayTarget] = useState(null);

    const loadReports = useCallback(async (page = 1) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get('/admin/payments/reports', { params: { page, status: status || undefined, provider_id: providerId || undefined } });
            setReports(res.data);
        } catch (err) {
            setMessage({ variant: 'danger', text: apiError(err, 'No se pudieron cargar los reportes.') });
        } finally {
            setIsLoading(false);
        }
    }, [status, providerId, setIsLoading]);

    const loadAgreements = useCallback(async (page = 1) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get('/admin/payments/agreements', {
                params: { page, has_payables: filter === '' ? undefined : filter, provider_id: providerId || undefined, q: query.trim() || undefined },
            });
            setAgreements(res.data);
        } catch (err) {
            setMessage({ variant: 'danger', text: apiError(err, 'No se pudieron cargar los contratos.') });
        } finally {
            setIsLoading(false);
        }
    }, [filter, query, providerId, setIsLoading]);

    useEffect(() => {
        if (!allowed) return;
        axiosInstance.get('/admin/payments/providers')
            .then((res) => setProviders(Array.isArray(res.data) ? res.data : []))
            .catch(() => setProviders([]));
    }, [allowed]);

    useEffect(() => { if (allowed) loadReports(1); }, [allowed, loadReports]);
    useEffect(() => { if (allowed) loadAgreements(1); }, [allowed, loadAgreements]);

    if (!allowed) return <Navigate to="/main" replace />;

    const refreshAll = () => { loadReports(reports?.current_page || 1); loadAgreements(agreements?.current_page || 1); };

    // ---- Acciones ----
    const withSessions = (text, data) => (
        data && data.sessions_closed != null ? `${text} Sesiones cerradas: ${data.sessions_closed}.` : text
    );

    const submitReview = async () => {
        if (reviewing) return;
        setReviewing(true);
        try {
            const res = await axiosInstance.put(`/admin/payments/reports/${review.report.id}/review`, {
                decision: review.decision,
                review_note: reviewNote.trim() || null,
                close_sessions: review.decision === 'confirm' && closeSessions,
            });
            setMessage({ variant: 'success', text: review.decision === 'confirm' ? withSessions('Pago confirmado y aviso apagado.', res.data) : 'Reporte rechazado.' });
        } catch (err) {
            setMessage({ variant: 'danger', text: apiError(err) });
        } finally {
            setReviewing(false);
            setReview(null);
            setReviewNote('');
            refreshAll();
        }
    };

    const submitFlag = async () => {
        if (flagSaving) return;
        setFlagSaving(true);
        try {
            const res = await axiosInstance.put(`/admin/payments/agreements/${flagTarget.agreement.id}/flag`, {
                has_payables: flagTarget.turnOn,
                note: flagTarget.turnOn ? (flagNote.trim() || null) : null,
                close_sessions: closeSessions,
            });
            setMessage({ variant: 'success', text: withSessions(flagTarget.turnOn ? 'Aviso activado.' : 'Aviso apagado.', res.data) });
        } catch (err) {
            setMessage({ variant: 'danger', text: apiError(err) });
        } finally {
            setFlagSaving(false);
            setFlagTarget(null);
            setFlagNote('');
            refreshAll();
        }
    };

    const submitPayment = async (values) => {
        const res = await axiosInstance.post(`/admin/payments/agreements/${payTarget.id}/payments`, { ...values, close_sessions: closeSessions });
        setPayTarget(null);
        setMessage({ variant: 'success', text: withSessions('Pago registrado y aviso apagado.', res.data) });
        refreshAll();
    };

    const closeSessionsCheck = (
        <Form.Check
            type="checkbox"
            id="close-sessions-check"
            className="mt-3"
            checked={closeSessions}
            onChange={(e) => setCloseSessions(e.target.checked)}
            label="Cerrar las sesiones de los usuarios del proveedor"
        />
    );
    const closeSessionsHelp = closeSessions && (
        <p style={{ fontSize: 12, color: 'var(--abi-muted)', margin: '4px 0 0' }}>
            Deberán iniciar sesión de nuevo y perderán lo que tengan sin guardar. Úsalo en horas de poco uso.
        </p>
    );

    const agreementLabel = (a) => `${a.ext_id} — ${a.provider?.name} → ${a.customer?.name}`;

    return (
        <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <h5 style={{ fontWeight: 800, margin: 0 }}>
                    <i className="fa fa-credit-card" style={{ color: 'var(--abi-primary)', marginRight: 8 }} />
                    Cuentas por pagar
                </h5>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Form.Select size="sm" style={{ minWidth: 220 }} value={providerId} onChange={(e) => setProviderId(e.target.value)} aria-label="Filtrar por proveedor">
                        <option value="">Todos los proveedores</option>
                        {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Form.Select>
                    <Button size="sm" variant="outline-primary" onClick={refreshAll} title="Recargar reportes y contratos">
                        <i className="fa fa-refresh" /> Refrescar
                    </Button>
                </div>
            </div>

            {message && <Alert variant={message.variant} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}

            <Tabs defaultActiveKey="reports" className="mb-3">
                <Tab eventKey="reports" title="Reportes de pago">
                    <div style={{ maxWidth: 240, marginBottom: 12 }}>
                        <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="pending">Pendientes</option>
                            <option value="confirmed">Confirmados</option>
                            <option value="rejected">Rechazados</option>
                            <option value="">Todos</option>
                        </Form.Select>
                    </div>

                    {!reports || reports.data.length === 0 ? (
                        <Alert variant="info">No hay reportes para este filtro.</Alert>
                    ) : (
                        <>
                            <Table responsive className="report-table mb-0">
                                <thead>
                                    <tr><th>Recibido</th><th>Proveedor → Cliente</th><th>Fecha de pago</th><th>Referencia</th><th>Valor</th><th>Origen</th><th>Estado</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {reports.data.map((r) => (
                                        <tr key={r.id}>
                                            <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDateTime(r.created_at)}</td>
                                            <td style={{ fontSize: 12 }}>
                                                <strong>{r.agreement?.ext_id}</strong>
                                                <div>{r.agreement?.provider} → {r.agreement?.customer}</div>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.paid_at)}</td>
                                            <td style={{ fontSize: 12 }}>{r.reference}{r.notes && <div style={{ color: 'var(--abi-muted)' }}>{r.notes}</div>}</td>
                                            <td>{r.amount != null ? currencyFormater(r.amount) : '—'}</td>
                                            <td style={{ fontSize: 12 }}>{SOURCE_LABEL[r.source] || r.source}{r.reported_by ? ` (${r.reported_by})` : ''}</td>
                                            <td><StatusBadge status={r.status} />{r.review_note && <div style={{ fontSize: 11 }}>{r.review_note}</div>}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {r.status === 'pending' && (
                                                    <>
                                                        <Button size="sm" variant="success" className="me-1" onClick={() => { setReview({ report: r, decision: 'confirm' }); setReviewNote(''); setCloseSessions(false); }}>Confirmar</Button>
                                                        <Button size="sm" variant="outline-danger" onClick={() => { setReview({ report: r, decision: 'reject' }); setReviewNote(''); }}>Rechazar</Button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <Pager page={reports} onChange={loadReports} />
                        </>
                    )}
                </Tab>

                <Tab eventKey="agreements" title="Contratos">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        <Form.Select style={{ maxWidth: 220 }} value={filter} onChange={(e) => setFilter(e.target.value)}>
                            <option value="">Todos los contratos</option>
                            <option value="1">Con cuentas por pagar</option>
                            <option value="0">Al día</option>
                        </Form.Select>
                        <InputGroup style={{ maxWidth: 320 }}>
                            <Form.Control
                                placeholder="Buscar contrato, proveedor o cliente"
                                maxLength={100}
                                defaultValue={query}
                                onKeyDown={(e) => { if (e.key === 'Enter') setQuery(e.target.value); }}
                                onBlur={(e) => { if (e.target.value !== query) setQuery(e.target.value); }}
                            />
                        </InputGroup>
                    </div>

                    {!agreements || agreements.data.length === 0 ? (
                        <Alert variant="info">No hay contratos para este filtro.</Alert>
                    ) : (
                        <>
                            <Table responsive className="report-table mb-0">
                                <thead>
                                    <tr><th>Contrato</th><th>Proveedor</th><th>Cliente</th><th>Aviso</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {agreements.data.map((a) => (
                                        <tr key={a.id}>
                                            <td><span className="product-card-extid">{a.ext_id}</span><div style={{ fontSize: 12 }}>{a.description}</div></td>
                                            <td style={{ fontSize: 12 }}>{a.provider?.name}</td>
                                            <td style={{ fontSize: 12 }}>{a.customer?.name}</td>
                                            <td style={{ fontSize: 12 }}>
                                                {a.has_payables ? (
                                                    <>
                                                        <span className="report-status-badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>Cuentas por pagar</span>
                                                        {a.note && <div>{a.note}</div>}
                                                        {a.flagged_at && <div style={{ color: 'var(--abi-muted)' }}>Desde {formatDateTime(a.flagged_at)}</div>}
                                                    </>
                                                ) : 'Al día'}
                                                {a.has_pending_report && <div><StatusBadge status="pending" /></div>}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {a.has_payables ? (
                                                    <Button size="sm" variant="outline-secondary" className="me-1" disabled={a.has_pending_report}
                                                        title={a.has_pending_report ? 'Revisa primero el reporte pendiente' : ''}
                                                        onClick={() => { setFlagTarget({ agreement: a, turnOn: false }); setCloseSessions(false); }}>Apagar aviso</Button>
                                                ) : (
                                                    <Button size="sm" variant="outline-warning" className="me-1"
                                                        onClick={() => { setFlagTarget({ agreement: a, turnOn: true }); setFlagNote(''); setCloseSessions(false); }}>Activar aviso</Button>
                                                )}
                                                <Button size="sm" variant="outline-primary" disabled={a.has_pending_report}
                                                    title={a.has_pending_report ? 'Revisa primero el reporte pendiente' : ''}
                                                    onClick={() => { setPayTarget(a); setCloseSessions(false); }}>Registrar pago</Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <Pager page={agreements} onChange={loadAgreements} />
                        </>
                    )}
                </Tab>
            </Tabs>

            {/* Confirmar / rechazar reporte */}
            <Modal show={!!review} onHide={reviewing ? undefined : () => setReview(null)} centered>
                <Modal.Header closeButton={!reviewing}>
                    <Modal.Title style={{ fontSize: 18 }}>{review?.decision === 'confirm' ? 'Confirmar pago' : 'Rechazar reporte'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {review && (
                        <p style={{ fontSize: 13 }}>
                            {agreementLabel({ ...review.report.agreement, ext_id: review.report.agreement?.ext_id })} · ref. <strong>{review.report.reference}</strong>
                            {review.decision === 'confirm' && ' — al confirmar se apagará el aviso del contrato.'}
                        </p>
                    )}
                    <Form.Group>
                        <Form.Label>{review?.decision === 'reject' ? 'Motivo del rechazo (obligatorio)' : 'Nota (opcional)'}</Form.Label>
                        <Form.Control as="textarea" rows={2} maxLength={500} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} />
                    </Form.Group>
                    {review?.decision === 'confirm' && <>{closeSessionsCheck}{closeSessionsHelp}</>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" disabled={reviewing} onClick={() => setReview(null)}>Cancelar</Button>
                    <Button variant={review?.decision === 'confirm' ? 'success' : 'danger'}
                        disabled={reviewing || (review?.decision === 'reject' && !reviewNote.trim())}
                        onClick={submitReview}>
                        {reviewing ? 'Guardando…' : (review?.decision === 'confirm' ? 'Confirmar' : 'Rechazar')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Activar / apagar aviso */}
            <Modal show={!!flagTarget} onHide={flagSaving ? undefined : () => setFlagTarget(null)} centered>
                <Modal.Header closeButton={!flagSaving}>
                    <Modal.Title style={{ fontSize: 18 }}>{flagTarget?.turnOn ? 'Activar aviso de cuentas por pagar' : 'Apagar aviso'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {flagTarget && <p style={{ fontSize: 13 }}>{agreementLabel(flagTarget.agreement)}</p>}
                    {flagTarget?.turnOn ? (
                        <>
                            <Form.Group>
                                <Form.Label>Nota visible para el proveedor (opcional)</Form.Label>
                                <Form.Control as="textarea" rows={2} maxLength={500} value={flagNote} onChange={(e) => setFlagNote(e.target.value)}
                                    placeholder="Ej.: Factura de octubre 2026 pendiente" />
                            </Form.Group>
                            <p style={{ fontSize: 12, color: 'var(--abi-muted)', marginTop: 8 }}>
                                Los usuarios del proveedor verán el aviso en toda la app. Los usuarios cliente nunca lo ven.
                            </p>
                        </>
                    ) : (
                        <p style={{ fontSize: 13 }}>El proveedor dejará de ver el aviso de este contrato.</p>
                    )}
                    {closeSessionsCheck}
                    {closeSessionsHelp}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" disabled={flagSaving} onClick={() => setFlagTarget(null)}>Cancelar</Button>
                    <Button variant={flagTarget?.turnOn ? 'warning' : 'primary'} disabled={flagSaving} onClick={submitFlag}>
                        {flagSaving ? 'Guardando…' : (flagTarget?.turnOn ? 'Activar' : 'Apagar')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Registrar pago directo */}
            <PaymentFormModal
                show={!!payTarget}
                onHide={() => setPayTarget(null)}
                title="Registrar pago"
                subtitle={payTarget ? `${agreementLabel(payTarget)}. Queda confirmado y apaga el aviso.` : ''}
                submitLabel="Registrar pago"
                onSubmit={submitPayment}
                extra={<>{closeSessionsCheck}{closeSessionsHelp}</>}
            />
        </div>
    );
}

const mapStateToProps = (state) => ({ roles: state.roles });
export default connect(mapStateToProps, { setIsLoading })(AdminPayments);
